(() => {
  const storageKey = 'vaillant:recently-viewed-products';
  const maximumStoredProducts = 12;

  const readProductHandles = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && handle) : [];
    } catch {
      return [];
    }
  };

  const writeProductHandles = (handles) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(handles.slice(0, maximumStoredProducts)));
    } catch {
      return;
    }
  };

  class VaillantRecentlyViewedRecorder extends HTMLElement {
    connectedCallback() {
      const handle = this.dataset.productHandle;
      if (!handle) return;

      const handles = readProductHandles().filter((storedHandle) => storedHandle !== handle);
      handles.unshift(handle);
      writeProductHandles(handles);
    }
  }

  class VaillantRecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-recently-viewed-grid]');
      this.limit = Math.min(Number(this.dataset.limit) || 4, 4);
      this.rootUrl = this.dataset.rootUrl || '/';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.chevronUrl = this.dataset.chevronUrl;
      this.previousLabel = this.dataset.previousLabel || 'Previous';
      this.nextLabel = this.dataset.nextLabel || 'Next';

      const handles = readProductHandles();
      if (!this.grid || !handles.length) {
        this.setVisible(false);
        return;
      }

      this.setVisible(true);
      this.renderSkeletons();
      this.loadProducts(handles);
    }

    setVisible(isVisible) {
      this.hidden = !isVisible;
      const heading = this.closest('.search-vaillant__inner')?.querySelector('.search-vaillant__recent-heading');
      if (heading) heading.hidden = !isVisible;
    }

    async loadProducts(handles) {
      const requests = handles.map(async (handle) => {
        try {
          const response = await fetch(`${this.rootUrl}products/${encodeURIComponent(handle)}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) return null;
          return response.json();
        } catch {
          return null;
        }
      });

      const products = (await Promise.all(requests))
        .filter((product) => product?.available)
        .slice(0, this.limit);

      if (!products.length) {
        this.setVisible(false);
        this.grid.replaceChildren();
        return;
      }

      this.grid.replaceChildren(...products.map((product) => this.createProductCard(product)));
    }

    renderSkeletons() {
      const skeletons = Array.from({ length: this.limit }, () => {
        const item = document.createElement('li');
        item.className = 'recently-viewed-vaillant__item';

        const skeleton = document.createElement('span');
        skeleton.className = 'recently-viewed-vaillant__skeleton';
        skeleton.setAttribute('aria-hidden', 'true');
        item.append(skeleton);

        return item;
      });

      this.grid.replaceChildren(...skeletons);
    }

    createProductCard(product) {
      const item = document.createElement('li');
      item.className = 'recently-viewed-vaillant__item';

      const card = document.createElement('product-card-vaillant');
      card.className = 'vaillant-product-card';
      card.dataset.currentProduct = String(product.id);
      card.dataset.showSecondaryImage = 'true';

      const mediaShell = document.createElement('div');
      mediaShell.className = 'vaillant-product-card__media-shell';

      const productView = document.createElement('div');
      productView.className = 'vaillant-product-card__product-view';
      productView.dataset.productView = String(product.id);

      const mediaLink = document.createElement('a');
      mediaLink.className = 'vaillant-product-card__media-link';
      mediaLink.href = product.url;
      mediaLink.ariaLabel = product.title;

      const gallery = document.createElement('div');
      gallery.className = 'vaillant-product-card__gallery';
      gallery.dataset.productGallery = '';

      const images = (product.images || []).map((image) => (typeof image === 'string' ? image : image.src)).filter(Boolean);

      if (images.length) {
        images.forEach((source, index) => gallery.append(this.createSlide(source, product.title, index)));
      } else {
        const slide = document.createElement('div');
        slide.className = 'vaillant-product-card__slide is-active is-loaded';
        slide.dataset.productSlide = '';
        slide.dataset.slideIndex = '0';
        gallery.append(slide);
      }

      mediaLink.append(gallery);
      productView.append(mediaLink);

      if (images.length > 1) {
        productView.append(this.createGalleryControls(), this.createGalleryProgress(images.length));
      }

      mediaShell.append(productView);

      const details = document.createElement('div');
      details.className = 'vaillant-product-card__details';

      const information = document.createElement('div');
      information.className = 'vaillant-product-card__information';
      information.dataset.productInformation = String(product.id);

      const title = document.createElement('h3');
      title.className = 'vaillant-product-card__title';

      const titleLink = document.createElement('a');
      titleLink.href = product.url;
      titleLink.textContent = product.title;
      title.append(titleLink);

      const price = document.createElement('div');
      price.className = 'vaillant-product-card__price';
      price.textContent = this.formatMoney(product.price);

      information.append(title, price);
      details.append(information);
      card.append(mediaShell, details);
      item.append(card);

      return item;
    }

    createSlide(source, title, index) {
      const slide = document.createElement('div');
      slide.className = `vaillant-product-card__slide${index === 0 ? ' is-active' : ''}`;
      slide.dataset.productSlide = '';
      slide.dataset.slideIndex = String(index);
      slide.setAttribute('aria-hidden', String(index !== 0));

      const skeleton = document.createElement('span');
      skeleton.className = 'vaillant-product-card__skeleton';
      skeleton.setAttribute('aria-hidden', 'true');

      const image = document.createElement('img');
      image.className = 'vaillant-product-card__image';
      image.src = this.resizeImage(source, 900);
      image.alt = title;
      image.loading = 'lazy';
      image.width = 900;
      image.height = 1186;

      slide.append(skeleton, image);
      return slide;
    }

    createGalleryControls() {
      const controls = document.createElement('div');
      controls.className = 'vaillant-product-card__gallery-controls';

      controls.append(
        this.createGalleryButton('previous', this.previousLabel),
        this.createGalleryButton('next', this.nextLabel)
      );

      return controls;
    }

    createGalleryButton(direction, label) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `vaillant-product-card__gallery-button vaillant-product-card__gallery-button--${direction}`;
      button.ariaLabel = label;
      button.dataset[direction === 'previous' ? 'galleryPrevious' : 'galleryNext'] = '';

      if (this.chevronUrl) {
        const icon = document.createElement('img');
        icon.src = this.chevronUrl;
        icon.alt = '';
        icon.width = 8;
        icon.height = 8;
        button.append(icon);
      }

      return button;
    }

    createGalleryProgress(imageCount) {
      const progress = document.createElement('div');
      progress.className = 'vaillant-product-card__gallery-progress';
      progress.dataset.galleryProgress = '';
      progress.setAttribute('aria-hidden', 'true');
      progress.style.setProperty('--gallery-slide-count', String(imageCount));
      progress.style.setProperty('--gallery-slide-index', '0');

      const indicator = document.createElement('span');
      indicator.className = 'vaillant-product-card__gallery-progress-indicator';
      progress.append(indicator);

      return progress;
    }

    resizeImage(source, width) {
      try {
        const url = new URL(source, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch {
        return source;
      }
    }

    formatMoney(cents) {
      const formatWithDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
        const number = (Number(value) / 100).toFixed(precision);
        const parts = number.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
      };

      const formats = {
        amount: () => formatWithDelimiters(cents),
        amount_no_decimals: () => formatWithDelimiters(cents, 0),
        amount_with_comma_separator: () => formatWithDelimiters(cents, 2, '.', ','),
        amount_no_decimals_with_comma_separator: () => formatWithDelimiters(cents, 0, '.', ','),
        amount_with_space_separator: () => formatWithDelimiters(cents, 2, ' ', ','),
        amount_with_apostrophe_separator: () => formatWithDelimiters(cents, 2, "'", '.'),
      };

      const formatted = this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) =>
        formats[key] ? formats[key]() : formatWithDelimiters(cents)
      );
      const parser = document.createElement('div');
      parser.innerHTML = formatted;
      return parser.textContent.trim();
    }
  }

  if (!customElements.get('vaillant-recently-viewed-recorder')) {
    customElements.define('vaillant-recently-viewed-recorder', VaillantRecentlyViewedRecorder);
  }

  if (!customElements.get('recently-viewed-vaillant')) {
    customElements.define('recently-viewed-vaillant', VaillantRecentlyViewed);
  }
})();
